import { getLocaleString, hasAnyTranslation } from '@assets/i18n/config';
import type { AssetClass } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import type { TFunction } from 'i18next';
import { round } from 'lodash-es';
import {
  HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE,
  HOUSEHOLD_MODEL_PORTFOLIO_IPS_FIELDS,
  IPS_FIELD_DEFAULTS,
  IPS_FIELD_OPTIONS,
} from './householdModelPortfolio.constants';
import type {
  HouseholdModelPortfolioIpsField,
  HouseholdModelPortfolioIpsFieldKey,
  HouseholdModelPortfolioIpsTargetAllocationRow,
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioTargetAllocation,
  HouseholdModelPortfolioWidgetOptions,
} from './householdModelPortfolio.types';

type IpsFieldConfig = (typeof HOUSEHOLD_MODEL_PORTFOLIO_IPS_FIELDS)[number];

const getIpsTranslatedModelValue = (
  model: HouseholdModelPortfolioModel,
  modelField: IpsFieldConfig['modelField'],
): TranslatedString | undefined => {
  switch (modelField) {
    case 'translatedPortfolioDescription':
      return model.translatedPortfolioDescription;
    case 'translatedInvestmentObjective':
      return model.translatedInvestmentObjective;
    case 'translatedLiquidity':
      return model.translatedLiquidity;
    case 'translatedPortfolioDisclaimer':
      return model.translatedPortfolioDisclaimer;
    default:
      return undefined;
  }
};

const formatIpsTextValue = (
  translateBackendString: (translatedObject: unknown) => string,
  value?: TranslatedString | null,
): string => {
  const translated = translateBackendString(value ?? undefined);
  return translated.trim()
    ? translated
    : HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
};

export const isIpsFieldEnabled = (
  options: HouseholdModelPortfolioWidgetOptions = {},
  optionKey: HouseholdModelPortfolioIpsFieldKey,
): boolean => options[optionKey] ?? IPS_FIELD_DEFAULTS[optionKey];

export const getEnabledIpsFieldKeys = (
  options: HouseholdModelPortfolioWidgetOptions = {},
): HouseholdModelPortfolioIpsFieldKey[] =>
  IPS_FIELD_OPTIONS.filter((optionKey) =>
    isIpsFieldEnabled(options, optionKey),
  );

export const getHouseholdModelPortfolioIpsFieldLabel = (
  translateBackendString: (translatedObject: unknown) => string,
  config: IpsFieldConfig,
  options: HouseholdModelPortfolioWidgetOptions = {},
  t: TFunction<['modelPortfolios']>,
): string => {
  const customLabel = options[config.labelOptionKey];

  if (customLabel && hasAnyTranslation(customLabel)) {
    const translated = translateBackendString(customLabel);
    if (translated.trim()) {
      return translated;
    }
  }

  return t(
    config.fallbackLabelKey as 'modelPortfolios:portfolio.policyAndAssets.description',
  );
};

export const formatHouseholdModelPortfolioIpsAssetClassName = (
  translateBackendString: (translatedObject: unknown) => string,
  assetClass: AssetClass | undefined,
): string => {
  const translated = translateBackendString(assetClass?.translatedName);
  if (translated.trim()) {
    return translated;
  }

  const name = assetClass?.name?.trim();
  return name || HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
};

export const formatHouseholdModelPortfolioTargetAllocationPercentage = (
  value: number | undefined,
): string => {
  if (value === undefined || Number.isNaN(value)) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  return `${new Intl.NumberFormat(getLocaleString(), {
    maximumFractionDigits: 2,
  }).format(round(value, 2))}%`;
};

export const formatHouseholdModelPortfolioIpsRichTextValue = (
  translateBackendString: (translatedObject: unknown) => string,
  value?: TranslatedString | null,
): string => {
  if (!hasAnyTranslation(value ?? undefined)) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  const html = translateBackendString(value ?? undefined);
  return html.trim() ? html : HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
};

export const mapHouseholdModelPortfolioTargetAllocationRows = (
  translateBackendString: (translatedObject: unknown) => string,
  targetAllocations: HouseholdModelPortfolioTargetAllocation[] | undefined,
): HouseholdModelPortfolioIpsTargetAllocationRow[] => {
  if (!targetAllocations?.length) {
    return [];
  }

  return targetAllocations.map((allocation) => ({
    assetClassName: formatHouseholdModelPortfolioIpsAssetClassName(
      translateBackendString,
      allocation.secondaryAssetClass,
    ),
    min: formatHouseholdModelPortfolioTargetAllocationPercentage(
      allocation.min,
    ),
    max: formatHouseholdModelPortfolioTargetAllocationPercentage(
      allocation.max,
    ),
  }));
};

const getIpsTextField = (
  translateBackendString: (translatedObject: unknown) => string,
  config: IpsFieldConfig,
  model: HouseholdModelPortfolioModel,
  options: HouseholdModelPortfolioWidgetOptions,
  t: TFunction<['modelPortfolios']>,
): HouseholdModelPortfolioIpsField => {
  const value = formatIpsTextValue(
    translateBackendString,
    getIpsTranslatedModelValue(model, config.modelField),
  );

  return {
    id: config.showOptionKey,
    label: getHouseholdModelPortfolioIpsFieldLabel(
      translateBackendString,
      config,
      options,
      t,
    ),
    kind: config.kind,
    value,
  };
};

const getIpsRichTextField = (
  translateBackendString: (translatedObject: unknown) => string,
  config: IpsFieldConfig,
  model: HouseholdModelPortfolioModel,
  options: HouseholdModelPortfolioWidgetOptions,
  t: TFunction<['modelPortfolios']>,
): HouseholdModelPortfolioIpsField => ({
  id: config.showOptionKey,
  label: getHouseholdModelPortfolioIpsFieldLabel(
    translateBackendString,
    config,
    options,
    t,
  ),
  kind: 'richText',
  value: formatHouseholdModelPortfolioIpsRichTextValue(
    translateBackendString,
    getIpsTranslatedModelValue(model, config.modelField),
  ),
});

const getIpsTargetAllocationsField = (
  translateBackendString: (translatedObject: unknown) => string,
  config: IpsFieldConfig,
  model: HouseholdModelPortfolioModel,
  options: HouseholdModelPortfolioWidgetOptions,
  t: TFunction<['modelPortfolios']>,
): HouseholdModelPortfolioIpsField => ({
  id: config.showOptionKey,
  label: getHouseholdModelPortfolioIpsFieldLabel(
    translateBackendString,
    config,
    options,
    t,
  ),
  kind: 'targetAllocations',
  targetAllocations: mapHouseholdModelPortfolioTargetAllocationRows(
    translateBackendString,
    model.targetAllocations,
  ),
});

const getIpsField = (
  translateBackendString: (translatedObject: unknown) => string,
  config: IpsFieldConfig,
  model: HouseholdModelPortfolioModel,
  options: HouseholdModelPortfolioWidgetOptions,
  t: TFunction<['modelPortfolios']>,
): HouseholdModelPortfolioIpsField => {
  switch (config.kind) {
    case 'richText':
      return getIpsRichTextField(
        translateBackendString,
        config,
        model,
        options,
        t,
      );
    case 'targetAllocations':
      return getIpsTargetAllocationsField(
        translateBackendString,
        config,
        model,
        options,
        t,
      );
    default:
      return getIpsTextField(translateBackendString, config, model, options, t);
  }
};

export const buildHouseholdModelPortfolioIpsFields = (
  translateBackendString: (translatedObject: unknown) => string,
  model: HouseholdModelPortfolioModel | null,
  options: HouseholdModelPortfolioWidgetOptions = {},
  t: TFunction<['modelPortfolios']>,
): HouseholdModelPortfolioIpsField[] => {
  if (!model) {
    return [];
  }

  const enabledKeys = new Set(getEnabledIpsFieldKeys(options));

  return HOUSEHOLD_MODEL_PORTFOLIO_IPS_FIELDS.filter((config) =>
    enabledKeys.has(config.showOptionKey),
  ).map((config) =>
    getIpsField(translateBackendString, config, model, options, t),
  );
};
